import { Variant, VariantType } from '@/models'

import { Permission, PermissionType } from './Permission'

export interface PermissionResolver {
    hasPermission: (permission: PermissionType) => boolean
}

export class CancelledCompanySubscriberPermissionResolver implements PermissionResolver {
    private readonly permissions: Set<PermissionType> = new Set([
        Permission.TAXES,
        Permission.MESSAGES,
        Permission.TAX_RETURN,
        Permission.QUARTERLY_TAX_RETURN,
        Permission.QUARTERLY_TAX_RETURNS_ANNUAL_SUMMARY,
        Permission.COMPANY_TAX_RETURN,
        Permission.DASHBOARD_LINK,
        Permission.BOOKKEEPING,
        Permission.CANCELLED_SUBSCRIPTION_DASHBOARD,
    ])

    hasPermission(permission: PermissionType) {
        return this.permissions.has(permission)
    }
}

export class CancelledSelfEmployedSubscriberPermissionResolver implements PermissionResolver {
    private readonly permissions: Set<PermissionType> = new Set([
        Permission.TAXES,
        Permission.MESSAGES,
        Permission.TAX_RETURN,
        Permission.QUARTERLY_TAX_RETURN,
        Permission.QUARTERLY_TAX_RETURNS_ANNUAL_SUMMARY,
        Permission.DASHBOARD_LINK,
        Permission.BOOKKEEPING,
        Permission.CANCELLED_SUBSCRIPTION_DASHBOARD,
    ])

    hasPermission(permission: PermissionType) {
        return this.permissions.has(permission)
    }
}

export class SelfEmployedSubscriberOnboardedBasicPermissionResolver implements PermissionResolver {
    private readonly permissions: Set<PermissionType> = new Set([
        Permission.TAXES,
        Permission.TAX_RETURN,
        Permission.QUARTERLY_TAX_RETURN,
        Permission.QUARTERLY_TAX_RETURNS_ANNUAL_SUMMARY,
        Permission.SELF_EMPLOYED_DASHBOARD,
        Permission.DASHBOARD_LINK,
        Permission.BOOKKEEPING,
        Permission.MESSAGES,
    ])

    hasPermission(permission: PermissionType) {
        return this.permissions.has(permission)
    }
}

export class SelfEmployedSubscriberOnboardedPremiumPermissionResolver implements PermissionResolver {
    private readonly permissions: Set<PermissionType> = new Set([
        Permission.TAXES,
        Permission.TAX_RETURN,
        Permission.QUARTERLY_TAX_RETURN,
        Permission.QUARTERLY_TAX_RETURNS_ANNUAL_SUMMARY,
        Permission.PREMIUM_SELF_EMPLOYED_DASHBOARD,
        Permission.DASHBOARD_LINK,
        Permission.BOOKKEEPING,
        Permission.MESSAGES,
    ])

    hasPermission(permission: PermissionType) {
        return this.permissions.has(permission)
    }
}

export class SelfEmployedSubscriberOnboardingPermissionResolver implements PermissionResolver {
    private readonly permissions: Set<PermissionType> = new Set([
        Permission.SELF_EMPLOYED_ONBOARDING_DASHBOARD,
        Permission.DASHBOARD_LINK,
        Permission.BOOKKEEPING,
        Permission.MESSAGES,
    ])

    hasPermission(permission: PermissionType) {
        return this.permissions.has(permission)
    }
}

export class CompanySubscriberOnboardedPermissionResolver implements PermissionResolver {
    private readonly permissions: Set<PermissionType> = new Set([
        Permission.TAXES,
        Permission.COMPANY_TAX_RETURN,
        Permission.TAX_RETURN,
        Permission.QUARTERLY_TAX_RETURN,
        Permission.QUARTERLY_TAX_RETURNS_ANNUAL_SUMMARY,
        Permission.COMPANY_DASHBOARD,
        Permission.DASHBOARD_LINK,
        Permission.BOOKKEEPING,
        Permission.MESSAGES,
    ])

    hasPermission(permission: PermissionType) {
        return this.permissions.has(permission)
    }
}

export class CompanySubscriberOnboardingPermissionResolver implements PermissionResolver {
    private readonly permissions: Set<PermissionType> = new Set([
        Permission.COMPANY_DASHBOARD,
        Permission.DASHBOARD_LINK,
        Permission.BOOKKEEPING,
        Permission.MESSAGES,
    ])

    hasPermission(permission: PermissionType) {
        return this.permissions.has(permission)
    }
}

export class TaxReturnCustomerNotSelfEmployedPermissionResolver implements PermissionResolver {
    private readonly permissions: Set<PermissionType> = new Set([
        Permission.TAX_RETURN,
        Permission.TAXES,
        Permission.MESSAGES,
        Permission.RENTA_DASHBOARD,
        Permission.DASHBOARD_LINK,
    ])

    hasPermission(permission: PermissionType) {
        return this.permissions.has(permission)
    }
}

export class TaxReturnCustomerSelfEmployedPermissionResolver implements PermissionResolver {
    private readonly permissions: Set<PermissionType> = new Set([
        Permission.TAX_RETURN,
        Permission.TAXES,
        Permission.MESSAGES,
        Permission.RENTA_DASHBOARD,
        Permission.DASHBOARD_LINK,
        Permission.BOOKKEEPING,
        Permission.SUBSCRIPTION_CROSS_SELL,
    ])

    hasPermission(permission: PermissionType) {
        return this.permissions.has(permission)
    }
}

export class QtrAnnualSummaryCustomerPermissionResolver implements PermissionResolver {
    private readonly permissions: Set<PermissionType> = new Set([
        Permission.QUARTERLY_TAX_RETURNS_ANNUAL_SUMMARY,
        Permission.TAX_RETURN,
        Permission.BOOKKEEPING,
        Permission.TAXES,
        Permission.MESSAGES,
        Permission.QTR_ANNUAL_DASHBOARD,
        Permission.DASHBOARD_LINK,
        Permission.SUBSCRIPTION_CROSS_SELL,
    ])

    hasPermission(permission: PermissionType) {
        return this.permissions.has(permission)
    }
}

export class SelfEmployedVisitorPermissionResolver implements PermissionResolver {
    private readonly permissions: Set<PermissionType> = new Set([
        Permission.TAXES_SUBSCRIPTION_ONBOARDING,
        Permission.MESSAGES_SUBSCRIPTION_ONBOARDING,
        Permission.SELF_EMPLOYED_ONBOARDING_DASHBOARD,
        Permission.DASHBOARD_LINK,
        Permission.BOOKKEEPING,
    ])

    hasPermission(permission: PermissionType) {
        return this.permissions.has(permission)
    }
}

export class CompanyVisitorPermissionResolver implements PermissionResolver {
    private readonly permissions: Set<PermissionType> = new Set([
        Permission.TAXES_SUBSCRIPTION_ONBOARDING,
        Permission.MESSAGES_SUBSCRIPTION_ONBOARDING,
        Permission.COMPANY_DASHBOARD,
        Permission.DASHBOARD_LINK,
        Permission.BOOKKEEPING,
    ])

    hasPermission(permission: PermissionType) {
        return this.permissions.has(permission)
    }
}

export class VisitorPermissionResolver implements PermissionResolver {
    hasPermission() {
        return false
    }
}

interface UserDependencies {
    isOnboarding: boolean // false if has any product or active subscription
    hasSubscription: boolean
    hasCancelledSubscription: boolean
    subscriptionType: 'company' | 'selfemployed' | 'premiumSelfEmployed' | null
    isSelfEmployed: boolean
    variant: VariantType | null
    hasQtrAnnualSummaries: boolean
    hasCompletedCompanyOnboarding: boolean
    hasCompletedSelfEmployedOnboarding: boolean
}

export class PermissionResolverFactory {
    // Reference documentation on user types
    // https://www.notion.so/taxscouts/User-roles-permissions-in-ES-CUSTOMER-APP-1c1b80a57a0280b982b2ed796190b191?pvs=4

    createPermissionResolver(user: UserDependencies): PermissionResolver {
        if (user.isOnboarding) {
            return this.getOnboardingPermissionResolver(user.variant)
        }

        if (user.hasCancelledSubscription) {
            return this.getCancelledSubscriptionPermissionResolver(user.subscriptionType)
        }

        if (user.hasSubscription && user.subscriptionType) {
            return this.getActiveSubscriptionPermissionResolver(
                user.subscriptionType,
                user.hasCompletedCompanyOnboarding,
                user.hasCompletedSelfEmployedOnboarding
            )
        }

        if (user.hasQtrAnnualSummaries) {
            return new QtrAnnualSummaryCustomerPermissionResolver()
        }

        return this.getTaxReturnPermissionResolver(user.isSelfEmployed)
    }

    private getOnboardingPermissionResolver(variant: VariantType | null): PermissionResolver {
        if (this.isCompanyVariant(variant)) {
            return new CompanyVisitorPermissionResolver()
        }

        if (this.isSelfEmployedVariant(variant)) {
            return new SelfEmployedVisitorPermissionResolver()
        }

        if (this.isTaxReturnVariant(variant)) {
            return new VisitorPermissionResolver()
        }

        return new SelfEmployedVisitorPermissionResolver()
    }

    private getCancelledSubscriptionPermissionResolver(subscriptionType: string | null): PermissionResolver {
        return subscriptionType === 'company'
            ? new CancelledCompanySubscriberPermissionResolver()
            : new CancelledSelfEmployedSubscriberPermissionResolver()
    }

    private getActiveSubscriptionPermissionResolver(
        subscriptionType: string,
        hasCompletedCompanyOnboarding: boolean,
        hasCompletedSelfEmployedOnboarding: boolean
    ): PermissionResolver {
        if (subscriptionType === 'company') {
            return hasCompletedCompanyOnboarding
                ? new CompanySubscriberOnboardedPermissionResolver()
                : new CompanySubscriberOnboardingPermissionResolver()
        }

        if (subscriptionType === 'selfemployed') {
            return hasCompletedSelfEmployedOnboarding
                ? new SelfEmployedSubscriberOnboardedBasicPermissionResolver()
                : new SelfEmployedSubscriberOnboardingPermissionResolver()
        }

        if (subscriptionType === 'premiumSelfEmployed') {
            return hasCompletedSelfEmployedOnboarding
                ? new SelfEmployedSubscriberOnboardedPremiumPermissionResolver()
                : new SelfEmployedSubscriberOnboardingPermissionResolver()
        }

        return new SelfEmployedSubscriberOnboardedBasicPermissionResolver()
    }

    private getTaxReturnPermissionResolver(isSelfEmployed: boolean): PermissionResolver {
        if (isSelfEmployed) {
            return new TaxReturnCustomerSelfEmployedPermissionResolver()
        }
        return new TaxReturnCustomerNotSelfEmployedPermissionResolver()
    }

    private isCompanyVariant(variant: VariantType | null): boolean {
        return variant === Variant.company || variant === Variant.newCompany || variant === Variant.existingCompany
    }

    private isSelfEmployedVariant(variant: VariantType | null): boolean {
        return (
            variant === Variant.selfEmployed ||
            variant === Variant.newSelfEmployed ||
            variant === Variant.existingSelfEmployed
        )
    }

    private isTaxReturnVariant(variant: VariantType | null): boolean {
        return variant === Variant.quarterlyTaxReturnsAnnualSummary || variant === Variant.taxReturn
    }
}
